import { tracked } from "@glimmer/tracking";
import Controller, { inject as controller } from "@ember/controller";
import { action, computed } from "@ember/object";
import { service } from "@ember/service";
import { reload } from "discourse/helpers/page-reloader";
import { popupAjaxError } from "discourse/lib/ajax-error";
import {
  listColorSchemes,
  loadColorSchemeStylesheet,
  updateColorSchemeCookie,
} from "discourse/lib/color-scheme-picker";
import {
  INTERFACE_COLOR_MODES,
  SEND_SHORTCUT_ENTER,
  SEND_SHORTCUT_META_ENTER,
} from "discourse/lib/constants";
import { normalizeUnderstoodLanguages } from "discourse/lib/content-localization";
import { deepEqual } from "discourse/lib/object";
import { formatShortcut } from "discourse/lib/shortcut-format";
import {
  currentThemeId,
  listThemes,
  setLocalTheme,
} from "discourse/lib/theme-selector";
import { applyValueTransformer } from "discourse/lib/transformer";
import {
  setDefaultHomepage,
  siteDefaultHomepage,
} from "discourse/lib/utilities";
import { AUTO_DELETE_PREFERENCES } from "discourse/models/bookmark";
import { i18n } from "discourse-i18n";

// same as UserOption::HOMEPAGES
const USER_HOMES = {
  1: "latest",
  2: "categories",
  3: "unread",
  4: "new",
  5: "top",
  6: "bookmarks",
  7: "unseen",
  8: "hot",
};

const TEXT_SIZES = ["smallest", "smaller", "normal", "larger", "largest"];
const TITLE_COUNT_MODES = ["notifications", "contextual"];

export default class InterfaceController extends Controller {
  @service interfaceColor;
  @service session;
  @controller("preferences") preferencesController;

  @tracked selectedInterfaceColorModeId = null;
  currentThemeId = currentThemeId();
  previewingColorScheme = false;
  selectedDarkColorSchemeId = null;
  makeColorSchemeDefault = true;
  subpageTitle = i18n("user.preferences_nav.interface");

  @computed("model.id", "currentUser.id")
  get canPreviewColorScheme() {
    return deepEqual(this.model?.id, this.currentUser?.id);
  }

  @computed("model.id", "currentUser.id")
  get isViewingOwnProfile() {
    return deepEqual(this.model?.id, this.currentUser?.id);
  }

  @computed("makeThemeDefault")
  get saveAttrNames() {
    let attrs = [
      "external_links_in_new_tab",
      "dynamic_favicon",
      "enable_quoting",
      "enable_smart_lists",
      "automatically_unpin_topics",
      "allow_private_messages",
      "enable_allowed_pm_users",
      "homepage_id",
      "hide_presence",
      "text_size",
      "title_count_mode",
      "skip_new_user_tips",
      "seen_popups",
      "color_scheme_id",
      "dark_scheme_id",
      "bookmark_auto_delete_preference",
      "interface_color_mode",
      "enable_markdown_monospace_font",
      "send_shortcut",
      "automatically_translate",
      "understood_languages",
    ];

    if (this.siteSettings.allow_user_locale) {
      attrs.push("locale");
    }

    if (this.makeThemeDefault) {
      attrs.push("theme_ids");
    }

    return applyValueTransformer("preferences-save-attributes", attrs, {
      page: "interface",
    });
  }

  @computed("model.user_option.understood_languages.[]")
  get understoodLanguages() {
    return normalizeUnderstoodLanguages(
      this.model.user_option.understood_languages
    );
  }

  @computed()
  get availableLocales() {
    return (this.siteSettings.available_locales ?? []).map((locale) => ({
      ...locale,
      id: locale.value,
    }));
  }

  @computed("currentThemeId")
  get defaultDarkSchemeId() {
    const theme = this.userSelectableThemes?.find(
      (t) => t.id === this.currentThemeId
    );
    return theme?.dark_color_scheme_id || -1;
  }

  @computed
  get textSizes() {
    return TEXT_SIZES.map((value) => {
      return { name: i18n(`user.text_size.${value}`), value };
    });
  }

  @computed("model.user_option.homepage_id", "userSelectableHome.[]")
  get homepageId() {
    return (
      this.model.user_option.homepage_id ||
      this.userSelectableHome.firstObject.value
    );
  }

  @computed
  get titleCountModes() {
    return TITLE_COUNT_MODES.map((value) => {
      return { name: i18n(`user.title_count_mode.${value}`), value };
    });
  }

  @computed
  get sendShortcutOptions() {
    return [
      {
        name: i18n("user.send_shortcut.enter"),
        value: SEND_SHORTCUT_ENTER,
      },
      {
        name: i18n("user.send_shortcut.meta_enter", {
          meta_key: formatShortcut("mod").label,
        }),
        value: SEND_SHORTCUT_META_ENTER,
      },
    ];
  }

  @computed
  get bookmarkAfterNotificationModes() {
    return Object.keys(AUTO_DELETE_PREFERENCES).map((key) => {
      return {
        value: AUTO_DELETE_PREFERENCES[key],
        name: i18n(`bookmarks.auto_delete_preference.${key.toLowerCase()}`),
      };
    });
  }

  @computed
  get userSelectableThemes() {
    return listThemes(this.site);
  }

  @computed("userSelectableThemes")
  get showThemeSelector() {
    return this.userSelectableThemes && this.userSelectableThemes.length > 1;
  }

  @computed("themeId")
  get themeIdChanged() {
    if (!this.isViewingOwnProfile) {
      return false;
    }

    if (this.currentThemeId === -1) {
      this.set("currentThemeId", this.themeId); // eslint-disable-line ember/no-side-effects
      return false;
    } else {
      return this.currentThemeId !== this.themeId;
    }
  }

  @computed("currentThemeId")
  get currentThemeForColorSchemes() {
    const theme = this.userSelectableThemes?.find(
      (t) => t.id === this.currentThemeId
    );
    return theme;
  }

  @computed("currentThemeId")
  get userSelectableColorSchemes() {
    return listColorSchemes(this.site, {
      currentTheme: this.currentThemeForColorSchemes,
    });
  }

  @computed("userSelectableThemes", "userSelectableColorSchemes", "themeId")
  get currentSchemeCanBeSelected() {
    if (!this.userSelectableThemes || !this.themeId) {
      return false;
    }

    const theme = this.userSelectableThemes.find((t) => t.id === this.themeId);
    if (!theme) {
      return false;
    }

    return this.userSelectableColorSchemes.find(
      (colorScheme) => colorScheme.id === theme.color_scheme_id
    );
  }

  @computed("model.user_option.theme_ids", "themeId")
  get showThemeSetDefault() {
    if (!this.isViewingOwnProfile) {
      return false;
    }

    return (
      !this.model?.user_option?.theme_ids ||
      this.model?.user_option?.theme_ids?.[0] !== this.themeId
    );
  }

  @computed("model.user_option.text_size", "textSize")
  get showTextSetDefault() {
    if (!this.isViewingOwnProfile) {
      return false;
    }

    return this.model?.user_option?.text_size !== this.textSize;
  }

  get isInLightMode() {
    return (
      this.interfaceColor.colorModeIsLight ||
      (this.interfaceColor.colorModeIsAuto &&
        !window.matchMedia("(prefers-color-scheme: dark)").matches)
    );
  }

  get isInDarkMode() {
    return (
      this.interfaceColor.colorModeIsDark ||
      (this.interfaceColor.colorModeIsAuto &&
        window.matchMedia("(prefers-color-scheme: dark)").matches)
    );
  }

  @computed()
  get userSelectableHome() {
    let homeValues = {};
    Object.keys(USER_HOMES).forEach((newValue) => {
      const newKey = USER_HOMES[newValue];
      homeValues[newKey] = newValue;
    });

    let result = [{ name: i18n("user.homepage.default"), value: -1 }];

    const siteHome = siteDefaultHomepage(this.siteSettings);
    const availableIds = this.siteSettings.top_menu
      .split("|")
      .filter((m) => m !== siteHome);
    availableIds.unshift(siteHome);

    const userHome = USER_HOMES[this.get("model.user_option.homepage_id")];

    if (userHome && !availableIds.includes(userHome)) {
      availableIds.push(USER_HOMES[this.homepageId]);
    }

    availableIds.forEach((m) => {
      let id = homeValues[m];
      if (id) {
        result.push({ name: i18n(`filters.${m}.title`), value: Number(id) });
      }
    });

    return result;
  }

  @computed("selectedDarkColorSchemeId", "currentThemeId")
  get showInterfaceColorModeSelector() {
    const theme = this.userSelectableThemes?.find(
      (t) => t.id === this.currentThemeId
    );
    return (
      (this.defaultDarkSchemeId > 0 &&
        theme.color_scheme_id &&
        theme.color_scheme_id !== theme.dark_color_scheme_id) ||
      this.selectedDarkColorSchemeId > 0
    );
  }

  @computed("currentThemeId")
  get userSelectableDarkColorSchemes() {
    return listColorSchemes(this.site, {
      darkOnly: true,
      currentTheme: this.currentThemeForColorSchemes,
    });
  }

  @computed("userSelectableColorSchemes", "userSelectableDarkColorSchemes")
  get showColorSchemeSelector() {
    return (
      this.showLightColorSchemeSelector ||
      this.showDarkColorSchemeSelector ||
      this.showInterfaceColorModeSelector
    );
  }

  @computed("userSelectableColorSchemes", "currentThemeId")
  get showLightColorSchemeSelector() {
    const schemes = this.userSelectableColorSchemes;
    if (!schemes || schemes.length <= 1) {
      return false;
    }
    const theme = this.currentThemeForColorSchemes;
    if (theme?.only_theme_color_schemes) {
      return schemes.filter((s) => !s.is_dark).length > 1;
    }
    return true;
  }

  @computed("userSelectableDarkColorSchemes", "currentThemeId")
  get showDarkColorSchemeSelector() {
    const schemes = this.userSelectableDarkColorSchemes;
    if (!schemes || schemes.length <= 1) {
      return false;
    }
    const theme = this.currentThemeForColorSchemes;
    if (theme?.only_theme_color_schemes) {
      return schemes.filter((s) => s.is_dark).length > 1;
    }
    return true;
  }

  get interfaceColorModes() {
    return [
      {
        id: INTERFACE_COLOR_MODES.AUTO,
        name: i18n("user.color_schemes.interface_modes.auto"),
      },
      {
        id: INTERFACE_COLOR_MODES.LIGHT,
        name: i18n("user.color_schemes.interface_modes.light"),
      },
      {
        id: INTERFACE_COLOR_MODES.DARK,
        name: i18n("user.color_schemes.interface_modes.dark"),
      },
    ];
  }

  get selectedInterfaceColorMode() {
    if (this.selectedInterfaceColorModeId) {
      return this.selectedInterfaceColorModeId;
    }
    if (this.isViewingOwnProfile) {
      if (this.interfaceColor.colorModeIsAuto) {
        return INTERFACE_COLOR_MODES.AUTO;
      }
      if (this.interfaceColor.colorModeIsLight) {
        return INTERFACE_COLOR_MODES.LIGHT;
      }
      if (this.interfaceColor.colorModeIsDark) {
        return INTERFACE_COLOR_MODES.DARK;
      }
    }
    return this.model.user_option.interface_color_mode;
  }

  @action
  setInterfaceLanguage(locale) {
    this.model.set("locale", locale);
  }

  @action
  setUnderstoodLanguages(locales) {
    this.model.set(
      "user_option.understood_languages",
      normalizeUnderstoodLanguages(locales)
    );
  }

  homeChanged() {
    const siteHome = siteDefaultHomepage(this.siteSettings);

    if (this.model.canPickThemeWithCustomHomepage) {
      USER_HOMES[-1] = "custom";
    }

    const userHome = USER_HOMES[this.get("model.user_option.homepage_id")];

    setDefaultHomepage(userHome || siteHome);
  }

  getSelectedColorSchemeId() {
    if (!this.session.userColorSchemeId) {
      return;
    }

    const theme = this.userSelectableThemes?.find((t) => t.id === this.themeId);

    // we don't want to display the numeric ID of a scheme
    // when it is set by the theme but not marked as user selectable
    if (
      theme?.color_scheme_id === this.session.userColorSchemeId &&
      !this.userSelectableColorSchemes.find(
        (t) => t.id === this.session.userColorSchemeId
      )
    ) {
      return;
    } else {
      return this.session.userColorSchemeId;
    }
  }

  @action
  save() {
    this.set("saved", false);
    const makeThemeDefault = this.makeThemeDefault;
    if (makeThemeDefault) {
      this.set("model.user_option.theme_ids", [this.themeId]);
    }

    const makeTextSizeDefault = this.makeTextSizeDefault;
    if (makeTextSizeDefault) {
      this.set("model.user_option.text_size", this.textSize);
    }

    if (!this.showColorSchemeSelector) {
      this.set("model.user_option.color_scheme_id", null);
      this.set("model.user_option.dark_scheme_id", null);
    } else if (this.makeColorSchemeDefault) {
      this.set("model.user_option.color_scheme_id", this.selectedColorSchemeId);
      this.set(
        "model.user_option.dark_scheme_id",
        this.selectedDarkColorSchemeId
      );
      if (this.selectedInterfaceColorModeId) {
        this.set(
          "model.user_option.interface_color_mode",
          this.selectedInterfaceColorModeId
        );
      }
    }

    return this.model
      .save(this.saveAttrNames)
      .then(() => {
        this.set("saved", true);

        if (this.isViewingOwnProfile) {
          if (makeThemeDefault) {
            setLocalTheme([]);
          } else {
            setLocalTheme(
              [this.themeId],
              this.get("model.user_option.theme_key_seq")
            );
          }
          if (makeTextSizeDefault) {
            this.model.updateTextSizeCookie(null);
          } else {
            this.model.updateTextSizeCookie(this.textSize);
          }

          if (this.makeColorSchemeDefault) {
            updateColorSchemeCookie(null);
            updateColorSchemeCookie(null, { dark: true });
          } else {
            updateColorSchemeCookie(this.selectedColorSchemeId);

            if (
              this.defaultDarkSchemeId > 0 &&
              this.selectedDarkColorSchemeId === this.defaultDarkSchemeId
            ) {
              updateColorSchemeCookie(null, { dark: true });
            } else {
              updateColorSchemeCookie(this.selectedDarkColorSchemeId, {
                dark: true,
              });
            }
          }

          if (this.selectedInterfaceColorModeId) {
            const modeId = this.selectedInterfaceColorModeId;
            if (modeId === INTERFACE_COLOR_MODES.AUTO) {
              this.interfaceColor.useAutoMode();
            } else if (modeId === INTERFACE_COLOR_MODES.LIGHT) {
              this.interfaceColor.forceLightMode();
            } else if (modeId === INTERFACE_COLOR_MODES.DARK) {
              this.interfaceColor.forceDarkMode();
            }
          }

          this.homeChanged();

          if (this.themeId && this.themeId !== this.currentThemeId) {
            reload();
          }
        }

        if (this.selectedInterfaceColorModeId) {
          this.selectedInterfaceColorModeId = null;
        }
      })
      .catch(popupAjaxError);
  }

  @action
  selectTextSize(newSize) {
    if (this.isViewingOwnProfile) {
      const classList = document.documentElement.classList;

      TEXT_SIZES.forEach((name) => {
        const className = `text-size-${name}`;
        if (newSize === name) {
          classList.add(className);
        } else {
          classList.remove(className);
        }
      });

      // Force refresh when leaving this screen
      this.session.requiresRefresh = true;
    }

    this.set("textSize", newSize);
  }

  @action
  loadColorScheme(colorSchemeId) {
    this.setProperties({
      selectedColorSchemeId: colorSchemeId,
      previewingColorScheme: this.#shouldEnablePreview(false),
    });

    if (!this.isViewingOwnProfile) {
      return;
    }

    // only preview light schemes when in light mode
    if (!this.isInLightMode) {
      return;
    }

    this.#previewColorScheme(false);
  }

  @action
  loadDarkColorScheme(colorSchemeId) {
    this.setProperties({
      selectedDarkColorSchemeId: colorSchemeId,
      previewingColorScheme: this.#shouldEnablePreview(true),
    });

    if (!this.isViewingOwnProfile) {
      return;
    }

    // only preview dark schemes when in dark mode
    if (!this.isInDarkMode) {
      return;
    }

    this.#previewColorScheme(true);
    this.session.set("darkModeAvailable", colorSchemeId !== -1);
  }

  @action
  selectColorMode(modeId) {
    this.selectedInterfaceColorModeId = modeId;
    this.set("previewingColorScheme", this.isViewingOwnProfile);

    if (!this.isViewingOwnProfile) {
      return;
    }

    this.#applyInterfaceModePreview(modeId);
    this.#previewColorSchemeForMode(modeId);
  }

  @action
  undoColorSchemePreview() {
    this.setProperties({
      selectedColorSchemeId: this.session.userColorSchemeId,
      selectedDarkColorSchemeId: this.session.userDarkSchemeId,
      selectedInterfaceColorModeId: null,
      previewingColorScheme: false,
    });

    if (this.isViewingOwnProfile) {
      const originalMode = this.model.user_option.interface_color_mode;
      if (originalMode === INTERFACE_COLOR_MODES.AUTO) {
        this.interfaceColor.useAutoMode();
      } else if (originalMode === INTERFACE_COLOR_MODES.LIGHT) {
        this.interfaceColor.forceLightMode();
      } else if (originalMode === INTERFACE_COLOR_MODES.DARK) {
        this.interfaceColor.forceDarkMode();
      }
    }

    const darkStylesheet = document.querySelector("link#cs-preview-dark"),
      lightStylesheet = document.querySelector("link#cs-preview-light");
    if (darkStylesheet) {
      darkStylesheet.remove();
    }

    if (lightStylesheet) {
      lightStylesheet.remove();
    }
  }

  @action
  resetSeenUserTips() {
    this.model.set("user_option.skip_new_user_tips", false);
    this.model.set("user_option.seen_popups", null);
    return this.model.save(["skip_new_user_tips", "seen_popups"]);
  }

  #shouldEnablePreview(isDarkMode) {
    return (
      this.isViewingOwnProfile &&
      (isDarkMode ? this.isInDarkMode : this.isInLightMode)
    );
  }

  #resolveThemeDefaultColorScheme(colorSchemeId, isDark) {
    // non-default color schemes
    if (!isDark && colorSchemeId >= 0) {
      return colorSchemeId;
    }
    // -1 is the default color scheme
    if (isDark && colorSchemeId !== -1) {
      return colorSchemeId;
    }

    const defaultTheme = this.userSelectableThemes.find(
      (theme) => theme.id === this.themeId
    );
    if (!defaultTheme) {
      return colorSchemeId;
    }

    if (isDark) {
      return defaultTheme.dark_color_scheme_id || this.selectedColorSchemeId;
    }
    return defaultTheme.color_scheme_id || colorSchemeId;
  }

  #applyInterfaceModePreview(modeId) {
    if (modeId === INTERFACE_COLOR_MODES.AUTO) {
      this.interfaceColor.useAutoMode();
    } else if (modeId === INTERFACE_COLOR_MODES.LIGHT) {
      this.interfaceColor.forceLightMode();
    } else if (modeId === INTERFACE_COLOR_MODES.DARK) {
      this.interfaceColor.forceDarkMode();
    }
  }

  #previewColorSchemeForMode(modeId) {
    if (this.#shouldShowPreviewForMode(modeId, false)) {
      this.#removePreviewStylesheet("dark");
      this.#previewColorScheme(false);
    } else if (this.#shouldShowPreviewForMode(modeId, true)) {
      this.#removePreviewStylesheet("light");
      this.#previewColorScheme(true);
    }
  }

  #shouldShowPreviewForMode(modeId, isDark) {
    const targetMode = isDark
      ? INTERFACE_COLOR_MODES.DARK
      : INTERFACE_COLOR_MODES.LIGHT;
    const autoCondition = isDark
      ? window.matchMedia("(prefers-color-scheme: dark)").matches
      : !window.matchMedia("(prefers-color-scheme: dark)").matches;

    return (
      modeId === targetMode ||
      (modeId === INTERFACE_COLOR_MODES.AUTO && autoCondition)
    );
  }

  #removePreviewStylesheet(type) {
    const selector =
      type === "dark" ? "link#cs-preview-dark" : "link#cs-preview-light";
    const stylesheet = document.querySelector(selector);
    if (stylesheet) {
      stylesheet.remove();
    }
  }

  #previewColorScheme(isDark) {
    const selectedId = isDark
      ? this.selectedDarkColorSchemeId
      : this.selectedColorSchemeId;
    const colorSchemeId = this.#resolveThemeDefaultColorScheme(
      selectedId,
      isDark
    );

    if (isDark) {
      loadColorSchemeStylesheet(colorSchemeId, this.themeId, true);
    } else {
      loadColorSchemeStylesheet(colorSchemeId, this.themeId, false);
      loadColorSchemeStylesheet(colorSchemeId, this.themeId, true);
    }
  }
}
